import React from 'react';
import { Squad, User } from '../../../types';
import { getCurriculumAdapterById } from '../../curriculum/adapters/registry';
import { toDateSafe } from '../../../shared/utils/firestoreDates';
import { ClassroomTeam, PortfolioEntry, visiblePortfolioPhotos } from '../classroom/types';

interface SessionStatsAccordionProps {
  squad: Squad;
  students: User[];
  achievementProgress: Record<string, Record<string, number>>; // studentId -> gameId -> count
  /** Classroom portfolio context — when present, show work-uploaded status */
  teams?: ClassroomTeam[];
  entries?: PortfolioEntry[];
  onUploadWork?: () => void;
}

function isSquadMissionComplete(squad: Squad, levelId: string, missionId: string): boolean {
  const raw = squad.missionProgress?.[levelId]?.[missionId];
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw > 0;
  return false;
}

function studentHasUploadedWork(
  studentId: string,
  levelId: string,
  teams: ClassroomTeam[],
  entries: PortfolioEntry[]
): boolean {
  const team = teams.find((t) => !t.archived && t.memberIds.includes(studentId));
  if (!team) return false;
  const entry = entries.find((e) => e.teamId === team.id && e.levelId === levelId);
  return visiblePortfolioPhotos(entry?.photos).length > 0;
}

const SessionStatsAccordion: React.FC<SessionStatsAccordionProps> = ({
  squad,
  students,
  achievementProgress,
  teams,
  entries,
  onUploadWork,
}) => {
  const adapter = getCurriculumAdapterById(squad.curriculumId || 'music');
  const levelId = squad.currentLessonId || '';
  const level = levelId ? (adapter.getLevelById(levelId) as any) : null;
  const presentStudents = students.filter((s) => squad.attendance && squad.attendance[s.id]);

  const showWorkUploads = Array.isArray(teams) && Array.isArray(entries);

  const sessionStart = toDateSafe(squad.liveSession?.startTime);
  const sessionDate = sessionStart
    ? sessionStart.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  const attendanceCount = presentStudents.length;

  const getStudentMissionProgress = (mission: any, studentId: string) => {
    // Completion missions (and Stage-marked measurable without per-student game stats)
    // are squad-scoped. Stage Next persists via updateMissionProgress('set', 1).
    if (mission.type === 'completion' || (mission.type === 'measurable' && !mission.gameId)) {
      const isComplete = isSquadMissionComplete(squad, levelId, mission.id);
      return { isComplete, text: isComplete ? '✓' : '✗' };
    }

    if (mission.type === 'measurable' && mission.gameId) {
      const count = achievementProgress[studentId]?.[mission.gameId!] || 0;
      const target = (mission as any).target || 0;
      return {
        isComplete: count >= target,
        text: `${count}/${target}`,
      };
    }
    return { isComplete: false, text: 'N/A' };
  };

  const totalMissions = level?.missions?.length || 0;
  let completedMissionCount = 0;
  if (level?.missions) {
    level.missions.forEach((mission: any) => {
      if (mission.type === 'completion' || (mission.type === 'measurable' && !mission.gameId)) {
        if (isSquadMissionComplete(squad, levelId, mission.id)) {
          completedMissionCount++;
        }
      } else if (mission.type === 'measurable' && mission.gameId) {
        const isCompletedByAny = presentStudents.some((student) => {
          const count = achievementProgress[student.id]?.[mission.gameId!] || 0;
          return count >= mission.target;
        });
        if (isCompletedByAny) {
          completedMissionCount++;
        }
      }
    });
  }
  const overallProgress =
    totalMissions > 0 ? Math.round((completedMissionCount / totalMissions) * 100) : 0;

  const uploadedCount = showWorkUploads
    ? presentStudents.filter((s) =>
        studentHasUploadedWork(s.id, levelId, teams!, entries!)
      ).length
    : 0;

  return (
    <div className="form-section">
      <div className="accordion-header">
        <h3 className="accordion-title">
          Level Summary
          <span className="overall-progress-badge">{overallProgress}% Squad Progress</span>
        </h3>
      </div>
      <div className="accordion-content">
        <div className="stats-grid">
          <div className="stat-item">
            <h4>Squad</h4>
            <p>{squad.title}</p>
          </div>
          <div className="stat-item">
            <h4>Lesson</h4>
            <p>{level?.title || 'N/A'}</p>
          </div>
          <div className="stat-item">
            <h4>Teacher</h4>
            <p>{squad.liveSession?.teacherName || 'N/A'}</p>
          </div>
          <div className="stat-item">
            <h4>Date</h4>
            <p>{sessionDate || 'N/A'}</p>
          </div>
          <div className="stat-item">
            <h4>Attendance</h4>
            <p>
              {attendanceCount} / {squad.studentIds.length} students
            </p>
          </div>
          {showWorkUploads ? (
            <div className="stat-item">
              <h4>Work uploaded</h4>
              <p>
                {uploadedCount} / {attendanceCount} present
              </p>
            </div>
          ) : null}
        </div>

        {showWorkUploads && onUploadWork ? (
          <div className="feedback-upload-row">
            <p className="feedback-upload-copy">
              Missing worksheet photos? Upload a team’s work before you finish.
            </p>
            <button type="button" className="feedback-upload-btn" onClick={onUploadWork}>
              Upload worksheet photo
            </button>
          </div>
        ) : null}

        <div className="student-stats-breakdown">
          <h4>Student Progress</h4>
          {showWorkUploads ? (
            <div className="student-stat-legend">
              <span>
                <span className="mission-status-pill completed">✓</span> mission done
              </span>
              <span>
                <span className="mission-status-pill">✗</span> mission open
              </span>
              <span>
                <span className="work-status-pill has-work">📷</span> work uploaded
              </span>
              <span>
                <span className="work-status-pill">—</span> no photo yet
              </span>
            </div>
          ) : null}
          {presentStudents.map((student) => {
            const hasWork = showWorkUploads
              ? studentHasUploadedWork(student.id, levelId, teams!, entries!)
              : null;
            return (
              <div key={student.id} className="student-stat-row">
                <span className="student-name">{student.displayName}</span>
                <div className="student-missions">
                  {level?.missions?.map((mission: any) => {
                    const progress = getStudentMissionProgress(mission, student.id);
                    return (
                      <span
                        key={mission.id}
                        className={`mission-status-pill ${progress.isComplete ? 'completed' : ''}`}
                        title={`${mission.text} - ${progress.text}`}
                      >
                        {progress.text}
                      </span>
                    );
                  })}
                  {showWorkUploads ? (
                    <span
                      className={`work-status-pill ${hasWork ? 'has-work' : 'no-work'}`}
                      title={
                        hasWork
                          ? 'Team worksheet photo uploaded'
                          : 'No worksheet photo for this student’s team yet'
                      }
                    >
                      {hasWork ? '📷' : '—'}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SessionStatsAccordion;
